<template>
	<div
		:class="
			bemm('', [
				status,
				size,
				Boolean(status) ? 'has-status' : '',
				hasError || error ? 'has-error' : '',
				readonly ? 'readonly' : '',
				disabled ? 'disabled' : '',
				fillHeight ? 'fill-height' : '',
				direction !== 'auto' ? direction : '',
				fit ? 'fit' : '',
				inlineEdit ? 'inline-edit' : '',
				inlineEdit && inlineEditActive ? 'inline-edit-active' : '',
			])
		"
		:style="inputStyles"
	>
		<BaseField
			:id="id"
			:label="label"
			:description="description"
			:hide-meta="hideMeta || (!label && !description)"
			:status="status"
			:error="error"
			:direction="direction"
			:fit="fit"
			:required="required"
			:container-class="bemm('container')"
			:meta-class="bemm('meta')"
			:label-class="bemm('label')"
			:label-text-class="bemm('label-text')"
			:required-class="bemm('required')"
			:description-class="bemm('description')"
			:control-class="bemm('control', [fit ? 'fit' : ''])"
			:control-id="id ? `input-${id}` : undefined"
			:error-class="bemm('error')"
		>
			<template #label-after>
				<slot name="label-after" />
				<div v-if="visibleLabelActions.length" :class="bemm('label-actions')">
					<Button
						v-for="action in visibleLabelActions"
						:key="action.id || action.icon"
						:class="bemm('label-action')"
						size="small"
						variant="ghost"
						:icon="action.icon"
						:aria-label="action.ariaLabel || action.tooltip || ''"
						:tooltip="action.tooltip"
						:disabled="disabled || action.disabled"
						@click="action.action?.()"
					/>
				</div>
			</template>
			<slot name="before"></slot>
			<div :class="bemm('field-wrap')">
				<slot name="field">
					<input
						:id="id"
						:type="type"
						:value="modelValue"
						:placeholder="placeholder"
						:autocomplete="autocomplete"
						:required="required"
						class="ar-input__field"
						v-bind="$attrs"
						:readonly="readonly"
						:disabled="disabled"
						@input="
							$emit(
								'update:modelValue',
								($event.target as HTMLInputElement).value
							);
							$emit('input', ($event.target as HTMLInputElement).value);
						"
						@blur="$emit('blur', $event)"
						@focus="$emit('focus', $event)"
					/>
				</slot>
			</div>
			<div
				v-if="$slots.after || showClearButton || visibleInputActions.length"
				:class="bemm('suffix')"
			>
				<slot name="after"></slot>
				<div v-if="visibleInputActions.length" :class="bemm('actions')">
					<Button
						v-for="action in visibleInputActions"
						:key="action.id || action.icon"
						:class="bemm('action')"
						size="small"
						variant="ghost"
						:icon="action.icon"
						:aria-label="action.ariaLabel || action.tooltip || ''"
						:tooltip="action.tooltip"
						:disabled="disabled || action.disabled"
						@mousedown.prevent
						@click="action.action?.()"
					/>
				</div>
				<Button
					v-if="showClearButton"
					:class="bemm('clear')"
					size="small"
					variant="naked"
					:aria-label="$t('common.aria.clearInput')"
					:icon="Icons.CROSS_SMALL"
					@mousedown.prevent
					@click="clearInput"
				>
				</Button>
			</div>
			<div v-if="$slots.overlay" :class="bemm('overlay')">
				<slot name="overlay"></slot>
			</div>
		</BaseField>
	</div>
</template>

<script lang="ts" setup>
import { useBemm } from 'bemm';
import { computed, PropType } from 'vue';
import { Size } from '@arritech/shared/types';
import { Icons } from '../Icon';
import { Button } from '../Button';
import BaseField from './BaseField.vue';

interface BaseInputAction {
	id?: string;
	icon: string;
	tooltip?: string;
	ariaLabel?: string;
	disabled?: boolean;
	action?: () => void;
}

const props = defineProps({
	modelValue: {
		type: [String, Number, Array],
		default: '',
	},
	id: {
		type: String,
		default: null,
	},
	label: {
		type: String,
		default: '',
	},
	description: {
		type: String,
		default: '',
	},
	hideMeta: {
		type: Boolean,
		default: false,
	},
	placeholder: {
		type: String,
		default: '',
	},
	autocomplete: {
		type: String,
		default: null,
	},
	type: {
		type: String,
		default: 'text',
	},
	status: {
		type: String,
		default: null,
	},
	error: {
		type: String,
		default: '',
	},
	readonly: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	clearable: {
		type: Boolean,
		default: false,
	},
	fillHeight: {
		type: Boolean,
		default: false,
	},
	fit: {
		type: Boolean,
		default: false,
	},
	size: {
		type: String as PropType<Size>,
		default: Size.MEDIUM,
	},
	direction: {
		type: String as PropType<'auto' | 'row' | 'column'>,
		default: 'auto',
	},
	required: {
		type: Boolean,
		default: false,
	},
	inlineEdit: {
		type: Boolean,
		default: false,
	},
	inlineEditActive: {
		type: Boolean,
		default: false,
	},
	inputActions: {
		type: Array as PropType<BaseInputAction[]>,
		default: () => [],
	},
	labelActions: {
		type: Array as PropType<BaseInputAction[]>,
		default: () => [],
	},
});

const emit = defineEmits([
	'update:modelValue',
	'input',
	'change',
	'blur',
	'focus',
	'clear',
]);

const hasError = computed(() => Boolean(props.error));
const bemm = useBemm('ar-input', {
	return: 'string',
	includeBaseClass: true,
});

const showClearButton = computed(() => {
	if (!props.clearable || props.readonly || props.disabled) {
		return false;
	}
	if (Array.isArray(props.modelValue)) {
		return props.modelValue.length > 0;
	}
	return (
		props.modelValue !== null &&
		props.modelValue !== undefined &&
		String(props.modelValue) !== ''
	);
});
const visibleInputActions = computed(() =>
	props.disabled ? [] : props.inputActions
);
const visibleLabelActions = computed(() =>
	props.disabled ? [] : props.labelActions
);

const clearInput = () => {
	const nextValue = Array.isArray(props.modelValue) ? [] : '';
	emit('update:modelValue', nextValue);
	emit('change', nextValue);
	emit('input', nextValue);
	emit('clear');
};

const inputStyles = computed(() => ({
	...(props.direction !== 'auto'
		? { '--form-direction': props.direction }
		: {}),

	'--input-status-color': `var(--color-${props.status || 'secondary'})`,
}));
</script>

<style lang="scss">
.ar-input {
	$b: &;
	--int-input-min-height: calc(var(--space) * 2.625);
	display: flex;
	flex-direction: var(--form-direction, column);
	gap: var(--space-xs);
	container-type: inline-size;

	width: var(--input-width, 100%);
	min-width: var(--input-min-width, 140px);

	@container (width > 400px) {
		#{$b}__container {
			flex-direction: var(--form-direction, row);
		}
		#{$b}__control {
			width: 100%;
		}
		#{$b}__meta {
			padding: var(--space-xs) 0;
			width: var(--input-label-width, max(30%, 150px));
			height: auto;
			min-width: 0;
		}

		&#{$b}--column {
			#{$b}__meta {
				width: 100%;
				padding: 0;
			}
		}
	}

	&--row {
		#{$b}__container {
			flex-direction: row;
			align-items: center;
		}
	}

	&__container {
		display: flex;
		flex-direction: column;
		gap: var(--space-xs);
		align-items: stretch;
		justify-content: center;
	}

	&__meta {
		display: flex;
		flex-direction: column;
		gap: calc(var(--space-xs) * 0.5);
	}

	&__label-actions {
		display: inline-flex;
		align-items: center;
		gap: var(--space-xs);
		margin-inline-start: var(--space-xs);
	}

	&__label-action {
		opacity: 0.8;
	}

	&__label-action:hover {
		opacity: 1;
	}

	&__control {
		flex-shrink: 1;
	}

	&__suffix {
		display: inline-flex;
		align-items: center;
		gap: var(--space-xs);
		flex-direction: row-reverse;
		flex-shrink: 0;
		margin-inline-end: var(--space-s);
	}

	&__overlay {
		position: absolute;
		inset-inline: 0;
		top: calc(100% + var(--space-xs));
		z-index: 8;
		pointer-events: none;
	}

	&__actions {
		display: inline-flex;
		align-items: center;
		gap: var(--space-xs);
	}

	&__field-wrap {
		flex: 1;
		min-width: 0;
		display: flex;
	}

	&__label {
		font-weight: var(--font-weight-medium);
		margin: 0;
		position: relative;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: flex;
		align-items: center;

		&[data-ellipsis-title] {
			cursor: help;
		}
	}

	&__label-text {
		display: block;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
		height: fit-content;
	}

	&__required {
		color: var(--color-error);
		margin-inline-start: var(--space-xs);
		font-weight: var(--font-weight-bold);
		font-size: var(--font-size-xs);
	}

	&__description {
		margin: 0;
		font-size: var(--font-size-s);
		color: color-mix(in srgb, var(--color-foreground), transparent 35%);
		line-height: 1.4;
	}

	&__control {
		display: flex;
		align-items: center;
		min-height: var(--input-min-height, var(--int-input-min-height));
		gap: var(--space-xs);
		position: relative;
		opacity: var(--input-opacity, 1);
		color: var(--color-foreground);
		font-size: 1em;
		border: 1px solid
			var(
				--input-border-color,
				var(
					--int-input-border-color,
					color-mix(in srgb, var(--color-foreground), transparent 80%)
				)
			);
		border-radius: var(--border-radius);
		background-color: var(
			--input-background,
			var(--int-input-background, var(--color-background))
		);
		box-shadow: 0 0 0 0 transparent;
		transition:
			border-color 0.2s,
			background-color 0.2s,
			box-shadow 0.2s;
		line-height: 1;

		&:focus-within {
			border-color: var(--input-status-color);
			box-shadow: 0 0 0 4px
				color-mix(
					in srgb,
					var(--input-status-color),
					var(--color-background) 80%
				);
		}
	}

	&__field {
		width: var(--input-control-width, 100%);
		flex: 1;
		min-width: 0;
		background-color: transparent;
		padding: var(
			--input-padding,
			var(--int-input-padding, var(--space-s) var(--space))
		);
		border: none;
		font-size: var(--font-size-s);
		line-height: 1.4;
		appearance: none;
		-webkit-appearance: none;
		outline: none;
		transition: padding-inline 0.2s ease;

		&:focus {
			outline: none;
		}

		&[type='search']::-webkit-search-decoration,
		&[type='search']::-webkit-search-cancel-button,
		&[type='search']::-webkit-search-results-button,
		&[type='search']::-webkit-search-results-decoration {
			-webkit-appearance: none;
		}
	}

	&__error {
		color: var(--color-error);
		font-size: var(--font-size-s);
	}

	&__clear {
		border: 0;
		background: transparent;
		padding: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		color: var(--color-red);
		opacity: 0.5;
		&:hover {
			opacity: 1;
		}
	}

	&__action {
		opacity: 0.8;
	}

	&__action:hover {
		opacity: 1;
	}

	&--has-error {
		--int-input-border-color: var(--color-error);
		--int-input-background: color-mix(
			in srgb,
			var(--color-error),
			var(--color-background) 90%
		);
	}

	&--success .ar-input__field {
		border-color: var(--color-success);
	}

	&--warning .ar-input__field {
		border-color: var(--color-warning);
	}

	&--error .ar-input__field {
		border-color: var(--color-error);
	}

	&--has-status .ar-input__field {
		border-color: var(--input-status-color);
	}

	&--disabled,
	&[disabled='disabled'] {
		opacity: 0.5;
		filter: grayscale(100%);
		--int-input-background: color-mix(
			in srgb,
			var(--color-foreground),
			var(--color-background) 90%
		);
		pointer-events: none;
	}
	&--readonly {
		--input-opacity: 0.5;

		#{$b}__container {
			pointer-events: none;
		}

		#{$b}__control {
			border-color: transparent;
			background-color: transparent;
		}

		&:not(:hover) #{$b}__field {
			padding-inline: 0;
		}
	}

	&--inline-edit {
		#{$b}__control {
			border-color: transparent;
			background-color: transparent;
			box-shadow: none;
		}

		#{$b}__control:focus-within {
			border-color: transparent;
			box-shadow: none;
		}

		#{$b}__control:hover,
		#{$b}__control:focus-within {
			border-color: var(
				--input-border-color,
				var(
					--int-input-border-color,
					color-mix(in srgb, var(--color-foreground), transparent 80%)
				)
			);
			background-color: var(
				--input-background,
				var(--int-input-background, var(--color-background))
			);
		}

		#{$b}__field {
			font-size: 1em;
			color: color-mix(in srgb, var(--color-foreground), transparent 10%);
		}

		&#{$b}--readonly {
			--input-opacity: 1;
			pointer-events: auto;

			#{$b}__container {
				pointer-events: auto;
			}

			#{$b}__control {
				cursor: text;
			}
		}
	}

	&--inline-edit-active {
		#{$b}__control {
			border-color: var(
				--input-border-color,
				var(
					--int-input-border-color,
					color-mix(in srgb, var(--color-foreground), transparent 80%)
				)
			);
			background-color: var(
				--input-background,
				var(--int-input-background, var(--color-background))
			);
		}
	}

	&--fill-height {
		height: 100%;
		#{$b}__container,
		#{$b}__input,
		#{$b}__control {
			height: 100%;
		}
	}

	&--small {
		font-size: 0.75em;
	}
	&--medium {
		font-size: 1em;
	}
	&--large {
		--int-input-padding: var(--space-m) var(--space-l);
		font-size: 1.25em;
	}
}
</style>
